import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from '@material-ui/core';
import Button from 'components/Button';
import TextField from 'components/TextField';
import { truckersAPI } from 'services/apiRest';
import { Container, WrapperInput, WrapperButtons, MotoristaList, MotoristaItem } from './styles';

export default function ModalAssociarMotorista({ open, onClose, onConfirm, frete }) {
  const [motoristas, setMotoristas] = useState([]);
  const [pesquisa, setPesquisa] = useState('');
  const [motoristaSelecionado, setMotoristaSelecionado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (open) {
      carregarMotoristas();
      setPesquisa('');
      setMotoristaSelecionado(frete?.motorista_id || null);
      setErro('');
    }
  }, [open, frete]);

  const carregarMotoristas = async () => {
    try {
      const data = await truckersAPI.listar();
      setMotoristas(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('[ModalAssociarMotorista] Erro ao carregar motoristas:', e);
      setMotoristas([]);
    }
  };

  const motoristasFiltrados = useMemo(() => {
    const term = pesquisa.toLowerCase().trim();
    if (!term) return motoristas;
    
    return motoristas.filter((m) => {
      const nome = (m.name || m.nome || '').toLowerCase();
      const cpf = (m.cpf || '').replace(/\D/g, '');
      const placa = (m.placa || '').toLowerCase();
      return nome.includes(term) || cpf.includes(term) || placa.includes(term);
    });
  }, [motoristas, pesquisa]);

  const handleConfirm = async () => {
    if (!motoristaSelecionado) {
      setErro('Selecione um motorista');
      return;
    }

    setLoading(true);
    try {
      await onConfirm(motoristaSelecionado);
      handleClose();
    } catch (error) {
      setErro(error.message || 'Erro ao associar motorista');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPesquisa('');
    setMotoristaSelecionado(null);
    setErro('');
    setLoading(false);
    onClose();
  };

  const formatCPF = (cpf) => {
    const digits = String(cpf || '').replace(/\D/g, '');
    if (digits.length === 11) {
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return cpf;
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Container>
        <h2 style={{ marginBottom: '20px' }}>Associar Motorista ao Frete</h2>

        {frete && (
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
            <div style={{ fontSize: '14px', marginBottom: '5px' }}>
              <strong>Frete:</strong> FRETE Nº {frete.numero || '-'}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {frete.collect_city && frete.delivery_city && (
                <span>{frete.collect_city}-{frete.collect_state} → {frete.delivery_city}-{frete.delivery_state}</span>
              )}
            </div>
            {frete.valor_frete && (
              <div style={{ fontSize: '12px', color: '#4caf50', marginTop: '5px' }}>
                Valor: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(frete.valor_frete)}
              </div>
            )}
          </div>
        )}

        <WrapperInput>
          <TextField
            label="Pesquisar Motorista"
            name="pesquisa"
            value={pesquisa}
            onChange={(e) => setPesquisa(e.target.value)}
            fullWidth
            color="secondary"
            placeholder="Digite nome, CPF ou placa"
            InputLabelProps={{ shrink: true }}
          />
        </WrapperInput>

        <MotoristaList>
          {motoristasFiltrados.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
              {pesquisa ? 'Nenhum motorista encontrado' : 'Nenhum motorista cadastrado'}
            </div>
          ) : (
            motoristasFiltrados.map((motorista) => (
              <MotoristaItem
                key={motorista.id}
                selected={motoristaSelecionado === motorista.id}
                onClick={() => {
                  setMotoristaSelecionado(motorista.id);
                  setErro('');
                }}
              >
                <div>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    {motorista.name || motorista.nome || 'Sem nome'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    CPF: {formatCPF(motorista.cpf)}
                  </div>
                  {motorista.placa && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      Placa: {motorista.placa}
                    </div>
                  )}
                </div>
                {motoristaSelecionado === motorista.id && (
                  <div style={{ color: '#4caf50', fontWeight: 'bold' }}>✓</div>
                )}
              </MotoristaItem>
            ))
          )}
        </MotoristaList>

        {erro && (
          <div style={{ color: '#f44336', marginTop: '10px', fontSize: '14px' }}>
            {erro}
          </div>
        )}

        <WrapperButtons>
          <Button
            label="Cancelar"
            type="button"
            onClick={handleClose}
            disabled={loading}
            style={{ backgroundColor: '#999' }}
          />
          <Button
            label={loading ? 'Associando...' : 'Confirmar'}
            type="button"
            onClick={handleConfirm}
            disabled={loading || !motoristaSelecionado}
          />
        </WrapperButtons>
      </Container>
    </Modal>
  );
}

